import React, { useMemo } from 'react'
import { Tooltip } from '@mui/material'
import { TreeStructureData, TreeStructureRow } from './types'
import { Column } from 'shared/ui/Table/types'
import { Table, TableProps } from 'shared/ui/Table/Table'
import { alternatingRowColor } from 'domains/scope-and-sequence/features/tree-creation/utility'
import { pluralizeCount } from 'shared/utils'
import { NodePathCell, nodePathString } from './NodePathCell'
import {
  LessonAssociation,
  LessonEntry,
} from 'domains/scope-and-sequence/types'

const columns: Column<TreeStructureRow>[] = [
  {
    title: 'Count',
    width: 40,
    rowContent: (l) => {
      return l.index
    },
  },
  {
    title: 'Unit',
    width: 40,
    rowContent: (l) => {
      return l.unitNum
    },
  },
  {
    title: 'Node Path',
    rowContent: (l) => {
      return <NodePathCell path={l.node_path} />
    },
    rowString(param: TreeStructureRow) {
      return nodePathString(param.node_path)
    },
  },
  {
    title: 'Lesson',
    rowContent: (l) => {
      return (
        <Tooltip title={l.lessonName} enterDelay={500} placement="top">
          <span>{l.lessonName}</span>
        </Tooltip>
      )
    },
  },
  {
    title: 'Standards',
    rowContent: (l) => {
      return l.standards.join(', ')
    },
  },
]

type LegacyProps = {
  data: TreeStructureData
  lessons?: never
  unitCount?: never
}

type LessonCentricProps = {
  data?: never
  lessons: LessonEntry[]
  unitCount: number
}

type CommonProps = {
  extraColumns?: Column<TreeStructureRow>[]
  overrideRowStyle?: (
    row: TreeStructureRow,
  ) => Record<string, string> | undefined
  hideColumns?: string[]
  lessonAssociations?: Record<string, LessonAssociation>
} & Pick<TableProps<TreeStructureRow>, 'toolBarItems'>

export function ScopeSequenceTreeStructureResponse(
  props: (LegacyProps | LessonCentricProps) & CommonProps,
) {
  const {
    extraColumns,
    overrideRowStyle,
    toolBarItems,
    hideColumns,
    lessonAssociations,
  } = props

  const { items, unitCount } = useMemo(() => {
    if (props.data) {
      return {
        items: convertToRows(props.data),
        unitCount: props.data.units.length,
      }
    }
    return {
      items: convertLessonsToRows(props.lessons, lessonAssociations),
      unitCount: props.unitCount,
    }
  }, [props.data, props.lessons, props.unitCount, lessonAssociations])

  const cols = useMemo(() => {
    let baseCols = columns
    if (hideColumns && hideColumns.length > 0) {
      baseCols = columns.filter(
        (col) =>
          typeof col.title !== 'string' || !hideColumns.includes(col.title),
      )
    }
    if (!extraColumns) return baseCols
    return [...baseCols, ...extraColumns]
  }, [extraColumns, hideColumns])

  return (
    <Table
      title={`Scope Sequence ${pluralizeCount('Lesson', items.length)} across ${pluralizeCount('Unit', unitCount)}`}
      columns={cols}
      items={items}
      getRowStyle={(idx) => {
        const item = items[idx]
        return overrideRowStyle?.(item) ?? alternatingRowColor(item.unitNum)
      }}
      toolBarItems={toolBarItems}
    />
  )
}

function convertToRows({ lessons, trees }: TreeStructureData) {
  return lessons.map<TreeStructureRow>((l, idx) => ({
    index: idx + 1,
    unitNum: idx + 1,
    lessonName: l.name,
    ...l,
    treeName: trees[[l.tree_id, l.version_id].join(':')] ?? '',
  }))
}

function convertLessonsToRows(
  lessons: LessonEntry[],
  associations?: Record<string, LessonAssociation>,
): TreeStructureRow[] {
  return lessons
    .map((lesson) => ({
      unitNum:
        associations?.[lesson.key]?.targetUnit ?? lesson.suggestedUnits[0] ?? 0,
      lessonName: lesson.name,
      tree_id: lesson.tree_id,
      version_id: lesson.version_id,
      node_id: lesson.node_id,
      node_path: lesson.node_path,
      treeName: lesson.treeName,
      standards: lesson.standards,
    }))
    .sort((a, b) => a.unitNum - b.unitNum)
    .map((row, idx) => ({ ...row, index: idx + 1 }))
}
